import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
    Container,
    Typography,
    Paper,
    Box,
    Button,
    Divider,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Alert,
    AlertTitle,
    Breadcrumbs,
    Link,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    useTheme,
} from '@mui/material';
import {
    NavigateNext as NavigateNextIcon,
    Security as SecurityIcon,
    VerifiedUser as VerifiedUserIcon,
    Warning as WarningIcon,
    CheckCircle as CheckCircleIcon,
    Videocam as VideocamIcon,
    ContactMail as ContactMailIcon,
    People as PeopleIcon,
    Lock as LockIcon,
    Gavel as GavelIcon,
    Home as HomeIcon,
    ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import Seo from '../../components/common/Seo';

function SafeRoomShare() {
    const theme = useTheme();

    const safetyChecks = [
        {
            title: '事前に十分なコミュニケーションを取る',
            description: 'メッセージでのやり取りだけでなく、可能であれば対面やビデオ通話で会話する機会を設けましょう。',
            icon: <VideocamIcon color="primary" />,
            items: [
                '直接会う前に十分なメッセージのやり取りをする',
                '可能であればビデオ通話で会話する',
                '初対面で会う場合は公共の場所を選ぶ',
                '共通の趣味や生活習慣について話し合う'
            ]
        },
        {
            title: '身元確認を行う',
            description: '安全のため、お互いの身元を確認することが重要です。',
            icon: <VerifiedUserIcon color="primary" />,
            items: [
                '本人確認書類（免許証など）を確認する',
                '現在の職業や収入源について確認する',
                '過去のルームシェア経験について質問する',
                '共通の知人がいれば紹介してもらう'
            ]
        },
        {
            title: '物件の安全性を確認する',
            description: '住む場所の安全性は最も重要です。以下のポイントを確認しましょう。',
            icon: <HomeIcon color="primary" />,
            items: [
                '防犯設備（オートロック、防犯カメラなど）の有無',
                '部屋の鍵がしっかりしているか',
                '非常口や避難経路の確認',
                '周辺地域の治安状況'
            ]
        },
        {
            title: '契約内容を明確にする',
            description: '入居前に契約内容をしっかり確認し、トラブルを未然に防ぎましょう。',
            icon: <GavelIcon color="primary" />,
            items: [
                '賃貸契約書の内容を確認する',
                'ルームシェアが許可されているか確認する',
                '解約条件や敷金の返還条件を確認する',
                '家賃や光熱費の支払い方法を明確にする'
            ]
        },
        {
            title: '生活ルールを明確に決める',
            description: '共同生活を快適に送るためには、事前にルールを決めておくことが大切です。',
            icon: <PeopleIcon color="primary" />,
            items: [
                '家賃や光熱費の分担方法',
                '共用スペースの利用ルール',
                '来客や宿泊の可否',
                '掃除や洗濯の当番制'
            ]
        },
        {
            title: '緊急連絡先を共有する',
            description: '万が一の時のために、緊急連絡先を共有しておきましょう。',
            icon: <ContactMailIcon color="primary" />,
            items: [
                'ルームメイト同士の緊急連絡先を共有する',
                '親族や友人などの緊急連絡先を共有する',
                '近くの警察署や病院の連絡先を確認しておく',
                '管理会社や大家さんの連絡先を共有する'
            ]
        },
    ];

    const warningSignsAccordions = [
        {
            title: '個人情報の取り扱いに注意',
            content: (
                <>
                    <Typography variant="body2" paragraph>
                        ルームシェアを始める際には、必要最低限の個人情報のみを開示するようにしましょう。特に初期段階では注意が必要です。
                    </Typography>
                    <List dense>
                        <ListItem>
                            <ListItemIcon>
                                <WarningIcon color="error" fontSize="small" />
                            </ListItemIcon>
                            <ListItemText
                                primary="SNSアカウントや勤務先などの詳細情報を早期に共有しない"
                            />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon>
                                <WarningIcon color="error" fontSize="small" />
                            </ListItemIcon>
                            <ListItemText
                                primary="銀行口座情報など重要な個人情報は契約が確定するまで開示しない"
                            />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon>
                                <CheckCircleIcon color="success" fontSize="small" />
                            </ListItemIcon>
                            <ListItemText
                                primary="信頼関係を構築してから徐々に必要な情報を共有する"
                            />
                        </ListItem>
                    </List>
                </>
            )
        },
        {
            title: '事前の支払いに注意',
            content: (
                <>
                    <Typography variant="body2" paragraph>
                        入居前の支払いについては特に注意が必要です。正規の契約を交わす前に多額の金銭を支払わないようにしましょう。
                    </Typography>
                    <List dense>
                        <ListItem>
                            <ListItemIcon>
                                <WarningIcon color="error" fontSize="small" />
                            </ListItemIcon>
                            <ListItemText
                                primary="契約書なしでの前払いや保証金の支払いを求められた場合は警戒する"
                            />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon>
                                <WarningIcon color="error" fontSize="small" />
                            </ListItemIcon>
                            <ListItemText
                                primary="現金での取引を強く求められた場合は注意する"
                            />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon>
                                <CheckCircleIcon color="success" fontSize="small" />
                            </ListItemIcon>
                            <ListItemText
                                primary="契約書を交わし、領収書を必ずもらう"
                            />
                        </ListItem>
                    </List>
                </>
            )
        },
        {
            title: '過度に急かす相手に注意',
            content: (
                <>
                    <Typography variant="body2" paragraph>
                        「すぐに決めてほしい」「今日中に契約したい」など、過度に急かす相手には注意が必要です。
                    </Typography>
                    <List dense>
                        <ListItem>
                            <ListItemIcon>
                                <WarningIcon color="error" fontSize="small" />
                            </ListItemIcon>
                            <ListItemText
                                primary="「今日中に決めないと他の人に決まる」などと過度に急かされる場合は警戒する"
                            />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon>
                                <WarningIcon color="error" fontSize="small" />
                            </ListItemIcon>
                            <ListItemText
                                primary="物件を実際に見る前に契約を急かされた場合は注意する"
                            />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon>
                                <CheckCircleIcon color="success" fontSize="small" />
                            </ListItemIcon>
                            <ListItemText
                                primary="自分のペースで決断し、十分な情報収集と検討を行う"
                            />
                        </ListItem>
                    </List>
                </>
            )
        },
        {
            title: '本人確認ができない相手に注意',
            content: (
                <>
                    <Typography variant="body2" paragraph>
                        相手の素性が確認できない場合や、本人確認を拒む場合は注意が必要です。
                    </Typography>
                    <List dense>
                        <ListItem>
                            <ListItemIcon>
                                <WarningIcon color="error" fontSize="small" />
                            </ListItemIcon>
                            <ListItemText
                                primary="顔写真や本名を開示しない、対面での会話を拒む場合は警戒する"
                            />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon>
                                <WarningIcon color="error" fontSize="small" />
                            </ListItemIcon>
                            <ListItemText
                                primary="SNSやプロフィールに不自然さがある場合は注意する"
                            />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon>
                                <CheckCircleIcon color="success" fontSize="small" />
                            </ListItemIcon>
                            <ListItemText
                                primary="安全のため、最初の顔合わせは公共の場所で行う"
                            />
                        </ListItem>
                    </List>
                </>
            )
        },
    ];

    return (
        <>
            <Seo
                title="安全なルームシェアのために - ルームシェアガイド"
                description="ルームシェアを安全に始めるための注意点や確認事項を解説。トラブルを未然に防ぐためのチェックポイントや、警戒すべき兆候などを紹介します。"
                path="/guide/safe-roomshare"
                keywords="ルームシェア安全対策,トラブル防止,身元確認,契約確認,共同生活ルール"
            />
            <Container maxWidth="md">
                <Paper sx={{ p: { xs: 2, md: 4 }, mt: 4, mb: 4, borderRadius: 2 }}>
                    {/* パンくずリスト */}
                    <Breadcrumbs
                        separator={<NavigateNextIcon fontSize="small" />}
                        aria-label="breadcrumb"
                        sx={{ mb: 3 }}
                    >
                        <Link component={RouterLink} to="/" color="inherit">
                            トップ
                        </Link>
                        <Link component={RouterLink} to="/guide" color="inherit">
                            ガイド
                        </Link>
                        <Typography color="text.primary">安全なルームシェアのために</Typography>
                    </Breadcrumbs>

                    <Typography
                        variant="h4"
                        component="h1"
                        gutterBottom
                        sx={{
                            fontWeight: 'bold',
                            fontSize: { xs: '1.8rem', md: '2.5rem' }
                        }}
                    >
                        安全なルームシェアのために
                    </Typography>

                    <Box sx={{ display: 'flex', alignItems: 'center', my: 2 }}>
                        <SecurityIcon color="primary" sx={{ mr: 1 }} />
                        <Typography variant="subtitle1" sx={{ fontWeight: 'medium', color: 'text.secondary' }}>
                            安全対策ガイド
                        </Typography>
                    </Box>

                    <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary' }}>
                        ルームシェアは新しい生活スタイルの選択肢として魅力的ですが、初めて会う人と生活を共にするには
                        一定の注意が必要です。このガイドでは、安全にルームシェアを始め、
                        トラブルを未然に防ぐためのポイントをご紹介します。
                    </Typography>

                    <Alert severity="info" sx={{ mb: 4 }}>
                        <AlertTitle>安全第一のルームシェア</AlertTitle>
                        ルームシェアを始める前に、相手の素性や物件の状況をしっかり確認しましょう。
                        少しでも不安や違和感を感じた場合は、無理をせず慎重に判断することが大切です。
                    </Alert>

                    <Divider sx={{ mb: 4 }} />

                    {/* 安全チェックポイント */}
                    <Typography variant="h5" component="h2" sx={{ mb: 3, fontWeight: 'bold' }}>
                        安全チェックポイント
                    </Typography>

                    {safetyChecks.map((check, index) => (
                        <Box key={index} sx={{ mb: 4 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        mr: 2,
                                    }}
                                >
                                    {check.icon}
                                </Box>
                                <Typography variant="h6" component="h3" sx={{ fontWeight: 'bold' }}>
                                    {check.title}
                                </Typography>
                            </Box>

                            <Typography variant="body1" sx={{ mb: 2, pl: 6 }}>
                                {check.description}
                            </Typography>

                            <Box sx={{ pl: 6 }}>
                                <ul>
                                    {check.items.map((item, i) => (
                                        <li key={i}>
                                            <Typography variant="body2" sx={{ mb: 1 }}>
                                                {item}
                                            </Typography>
                                        </li>
                                    ))}
                                </ul>
                            </Box>

                            {index < safetyChecks.length - 1 && (
                                <Divider sx={{ mt: 3 }} />
                            )}
                        </Box>
                    ))}

                    {/* 警戒サイン */}
                    <Typography variant="h5" component="h2" sx={{ mt: 5, mb: 3, fontWeight: 'bold' }}>
                        警戒すべき兆候
                    </Typography>

                    <Alert severity="warning" sx={{ mb: 4 }}>
                        <AlertTitle>注意が必要な状況</AlertTitle>
                        以下のような状況では特に注意が必要です。少しでも違和感を感じたら、慎重に判断しましょう。
                    </Alert>

                    {warningSignsAccordions.map((accordion, index) => (
                        <Accordion key={index} sx={{ mb: 2, '&:before': { display: 'none' } }}>
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                sx={{
                                    backgroundColor: 'rgba(255, 235, 59, 0.1)',
                                    '&:hover': {
                                        backgroundColor: 'rgba(255, 235, 59, 0.2)',
                                    }
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <WarningIcon color="warning" sx={{ mr: 2 }} />
                                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                        {accordion.title}
                                    </Typography>
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails>
                                {accordion.content}
                            </AccordionDetails>
                        </Accordion>
                    ))}

                    {/* トラブル発生時の対応 */}
                    <Typography variant="h5" component="h2" sx={{ mt: 5, mb: 3, fontWeight: 'bold' }}>
                        トラブル発生時の対応
                    </Typography>

                    <Box sx={{ mb: 4 }}>
                        <Typography variant="body1" paragraph>
                            万が一トラブルが発生した場合は、以下の対応を検討してください：
                        </Typography>

                        <List>
                            <ListItem>
                                <ListItemIcon>
                                    <LockIcon color="primary" />
                                </ListItemIcon>
                                <ListItemText
                                    primary="深刻な問題が発生した場合は警察や専門機関に相談する"
                                    secondary="特に安全が脅かされる場合は躊躇せず110番通報してください。"
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemIcon>
                                    <GavelIcon color="primary" />
                                </ListItemIcon>
                                <ListItemText
                                    primary="法的なトラブルの場合は法律の専門家に相談する"
                                    secondary="契約関連のトラブルは弁護士や消費生活センターに相談しましょう。"
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemIcon>
                                    <ContactMailIcon color="primary" />
                                </ListItemIcon>
                                <ListItemText
                                    primary="当サイトの通報機能を利用する"
                                    secondary="不審なユーザーや違反行為を見つけた場合は、サイト上の通報機能を利用してください。"
                                />
                            </ListItem>
                        </List>
                    </Box>

                    {/* まとめ */}
                    <Box sx={{ mt: 6, p: 3, bgcolor: '#ECEFF1', borderRadius: 2 }}>
                        <Typography variant="h6" gutterBottom>
                            安全なルームシェアのために
                        </Typography>
                        <Typography variant="body2" paragraph>
                            ルームシェアは初めての人との共同生活に不安を感じるかもしれませんが、
                            適切な準備と注意を払うことで、多くの人が安全で充実した共同生活を送っています。
                            このガイドを参考に、安全第一でルームシェアを始めてください。
                        </Typography>
                        <Typography variant="body2" paragraph>
                            不安なことがあれば、当サイトのサポートチームにいつでもご相談ください。
                        </Typography>
                        <Button
                            component={RouterLink}
                            to="/guide"
                            color="primary"
                            variant="contained"
                            endIcon={<NavigateNextIcon />}
                            sx={{ mt: 1 }}
                        >
                            ガイド一覧に戻る
                        </Button>
                    </Box>
                </Paper>
            </Container>
        </>
    );
}

export default SafeRoomShare; 